import { useEffect, useMemo, useState } from 'react'
import type { JSX } from 'react'

import {
  Banner,
  Badge,
  Button,
  Flex,
  Loader,
  Select,
  Text,
} from '@buffer-mono/popcorn'

import { useChannels } from '~publish/components/Composer/store/hooks/useChannels'
import { OpenChannelStoreFrontTrigger } from '~publish/components/OpenChannelStoreFrontTrigger/OpenChannelStoreFrontTrigger'
import { canPerform } from '~publish/utils/permissions/canPerform'

import { findBestChannelMatch, isKnownPlatform } from './channelMatcher'
import { extractSourceChannels } from '../CsvUploadStep/hootsuite'
import type { ChannelMapping, ImportItem } from '../MigrationWizard/types'

type ChannelMappingStepProps = {
  items: ImportItem[]
  onComplete: (mappings: ChannelMapping[]) => void
  onBack: () => void
}

export function ChannelMappingStep({
  items,
  onComplete,
  onBack,
}: ChannelMappingStepProps): JSX.Element {
  const { channels: channelsMap, loading } = useChannels({ skip: false })
  // An import writes posts, so a channel the actor cannot post to is not a
  // valid destination — every mapped post would fail at `createPost`.
  const channelsList = useMemo(
    () =>
      Object.values(channelsMap).filter((channel) =>
        canPerform(channel, 'manageUpdates'),
      ),
    [channelsMap],
  )

  const sourceChannels = useMemo(() => extractSourceChannels(items), [items])

  const [mappings, setMappings] = useState<ChannelMapping[]>([])

  // Auto-match on initial load
  useEffect(() => {
    if (loading) return
    if (mappings.length > 0) return // already initialized

    const initialMappings: ChannelMapping[] = []
    for (const [key, source] of sourceChannels) {
      const match = findBestChannelMatch(
        source.sourcePlatform,
        source.sourceAccountName,
        channelsList,
      )
      initialMappings.push({
        sourceKey: key,
        sourcePlatform: source.sourcePlatform,
        sourceAccountName: source.sourceAccountName,
        mappedChannelId: match?.channelId ?? null,
        postCount: source.postCount,
      })
    }
    setMappings(initialMappings)
  }, [loading, channelsList, sourceChannels, mappings.length])

  const handleMappingChange = (sourceKey: string, channelId: string): void => {
    setMappings((prev) =>
      prev.map((m) =>
        m.sourceKey === sourceKey
          ? { ...m, mappedChannelId: channelId === 'skip' ? null : channelId }
          : m,
      ),
    )
  }

  const mappedCount = mappings.filter((m) => m.mappedChannelId).length
  const skippedCount = mappings.length - mappedCount
  const skippedPostCount = mappings
    .filter((m) => !m.mappedChannelId)
    .reduce((sum, m) => sum + m.postCount, 0)

  const handleContinue = (): void => {
    onComplete(mappings)
  }

  if (loading) {
    return (
      <Flex
        align="center"
        justify="center"
        style={{ padding: 'var(--space-2xl)' }}
      >
        <Loader />
      </Flex>
    )
  }

  return (
    <Flex direction="column" gap="lg">
      <Flex direction="column" gap="xs">
        <Text color="subtle">
          We detected {sourceChannels.size} channel
          {sourceChannels.size !== 1 ? 's' : ''} in your export. Match each one
          to a connected Buffer channel, or skip channels you don&apos;t want to
          import.
        </Text>
      </Flex>

      <Flex direction="column" gap="md" fullWidth>
        {mappings.map((mapping) => {
          const known = isKnownPlatform(mapping.sourcePlatform)
          return (
            <Flex
              key={mapping.sourceKey}
              align="center"
              justify="between"
              gap="md"
              fullWidth
            >
              <Flex direction="column" gap="2xs" style={{ flex: 1 }}>
                <Flex align="center" gap="xs">
                  <Text weight="bold">{mapping.sourceAccountName}</Text>
                  <Badge size="small">{mapping.sourcePlatform}</Badge>
                </Flex>
                <Text color="subtle" size="sm">
                  {mapping.postCount} post{mapping.postCount !== 1 ? 's' : ''}
                </Text>
              </Flex>

              <Flex align="center" gap="xs" style={{ flex: 1 }}>
                {!known ? (
                  <Text color="subtle" size="sm">
                    Unsupported platform
                  </Text>
                ) : (
                  <Select
                    value={mapping.mappedChannelId ?? 'skip'}
                    onValueChange={(value: string) =>
                      handleMappingChange(mapping.sourceKey, value)
                    }
                  >
                    <Select.Trigger style={{ width: '100%' }} />
                    <Select.Content>
                      <Select.Item value="skip">Skip this channel</Select.Item>
                      <Select.Separator />
                      {channelsList
                        .filter((ch) => !ch.isDisconnected)
                        .map((ch) => (
                          <Select.Item key={ch.id} value={ch.id}>
                            {ch.displayName || ch.name} ({ch.service})
                          </Select.Item>
                        ))}
                    </Select.Content>
                  </Select>
                )}
              </Flex>
            </Flex>
          )
        })}
      </Flex>

      {skippedCount > 0 && (
        <Banner variant="warning">
          <Banner.Text>
            {skippedPostCount} post{skippedPostCount !== 1 ? 's' : ''} across{' '}
            {skippedCount} channel{skippedCount !== 1 ? 's' : ''} will be
            skipped.
          </Banner.Text>
        </Banner>
      )}

      {skippedCount > 0 && (
        <Flex>
          <OpenChannelStoreFrontTrigger cta="migration-assistant-connect-channel">
            <Button variant="tertiary" size="small">
              Connect a New Channel
            </Button>
          </OpenChannelStoreFrontTrigger>
        </Flex>
      )}

      <Flex gap="sm" justify="end">
        <Button variant="secondary" onClick={onBack}>
          Back
        </Button>
        <Button
          variant="primary"
          onClick={handleContinue}
          disabled={mappedCount === 0}
        >
          Continue to Review
        </Button>
      </Flex>
    </Flex>
  )
}
